/**
 * Query key factory for consistent cache key management.
 * Using a factory pattern ensures type safety and prevents typos.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys
 */

export const queryKeys = {
  // User/Auth keys
  me: ['/me'] as const,

  // Course keys
  courses: {
    all: ['/courses'] as const,
    lists: () => [...queryKeys.courses.all, 'list'] as const,
    list: <T extends object>(filters?: T) => [...queryKeys.courses.lists(), filters] as const,
    details: () => [...queryKeys.courses.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.courses.details(), id] as const,
    sections: (courseId: string) => [...queryKeys.courses.detail(courseId), 'sections'] as const,
    search: (term: string) => [...queryKeys.courses.all, 'search', term] as const,
    exists: (id: string) => [...queryKeys.courses.all, 'exists', id] as const,
  },

  // Student keys
  students: {
    all: ['/students'] as const,
    details: () => [...queryKeys.students.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.students.details(), id] as const,
    plans: (studentId: string) => [...queryKeys.students.detail(studentId), 'plans'] as const,
    plan: (studentId: string, planId: string) =>
      [...queryKeys.students.plans(studentId), planId] as const,
    enrollments: (studentId: string) =>
      [...queryKeys.students.detail(studentId), 'enrollments'] as const,
  },
  studentCoursePlans: {
    all: ['/student-course-plans'] as const,
    search: <T extends object>(request: T) =>
      [...queryKeys.studentCoursePlans.all, 'search', request] as const,
    list: (studentId: string) =>
      [...queryKeys.studentCoursePlans.all, 'student', studentId] as const,
    // Nested under `list(studentId)` so invalidating a student's plans also refreshes the drawer.
    staffAlerts: (studentId: string, planId: string) =>
      [...queryKeys.studentCoursePlans.list(studentId), 'staff-alerts', planId] as const,
    detail: (planId: string) => [...queryKeys.studentCoursePlans.all, planId] as const,
    clustersPathways: (planId: string) =>
      [...queryKeys.studentCoursePlans.detail(planId), 'clusters-pathways'] as const,
  },
  studentCoursePlanManagerFilterOptions: {
    all: ['/student-course-plan-manager-filter-options'] as const,
    status: () => [...queryKeys.studentCoursePlanManagerFilterOptions.all, 'status'] as const,
  },
  contacts: {
    all: ['/contacts'] as const,
    counselors: () => [...queryKeys.contacts.all, 'counselors'] as const,
  },
  subjectAreas: {
    all: ['/subject-areas'] as const,
    lists: () => [...queryKeys.subjectAreas.all, 'list'] as const,
    list: <T extends object>(filters?: T) => [...queryKeys.subjectAreas.lists(), filters] as const,
    details: () => [...queryKeys.subjectAreas.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.subjectAreas.details(), id] as const,
  },
  schoolStudentGroups: {
    all: ['/school-student-groups'] as const,
    list: () => [...queryKeys.schoolStudentGroups.all, 'list'] as const,
    listByHighSchool: () => [...queryKeys.schoolStudentGroups.all, 'list', 'highschool'] as const,
  },
  configurations: {
    all: ['/configurations'] as const,
    config: () => [...queryKeys.configurations.all, 'config'] as const,
  },
  courseCatalog: {
    all: ['/course-catalog'] as const,
    lists: () => [...queryKeys.courseCatalog.all, 'list'] as const,
    list: <T extends object>(filters?: T) => [...queryKeys.courseCatalog.lists(), filters] as const,
    details: () => [...queryKeys.courseCatalog.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.courseCatalog.details(), id] as const,
  },
  recommendations: {
    all: ['/recommendations'] as const,
    list: <T extends object>(filters?: T) =>
      [...queryKeys.recommendations.all, 'list', filters] as const,
    byCourse: (courseId: string) => [...queryKeys.recommendations.all, 'course', courseId] as const,
    students: <T extends object>(filters?: T) =>
      [...queryKeys.recommendations.all, 'students', filters] as const,
  },
  lookupData: {
    all: ['/lookup-data'] as const,
    instructionalLevels: () => [...queryKeys.lookupData.all, 'instructional-levels'] as const,
  },

  schools: {
    all: ['/schools'] as const,
    list: () => [...queryKeys.schools.all, 'list'] as const,
  },
  plansOfStudy: {
    all: ['/plans-of-study'] as const,
    listLite: () => [...queryKeys.plansOfStudy.all, 'list-lite'] as const,
    lists: () => [...queryKeys.plansOfStudy.all, 'list'] as const,
    list: <T extends object>(filters?: T) => [...queryKeys.plansOfStudy.lists(), filters] as const,
    details: () => [...queryKeys.plansOfStudy.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.plansOfStudy.details(), id] as const,
  },
  /** Specialization areas — list cache only; does not overlap POS / plans-of-study keys. */
  clusters: {
    all: ['/clusters'] as const,
    lists: () => [...queryKeys.clusters.all, 'list'] as const,
    list: <T extends object>(filters?: T) => [...queryKeys.clusters.lists(), filters] as const,
    details: () => [...queryKeys.clusters.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.clusters.details(), id] as const,
    /** Full cluster JSON for staff publish (matches GET /clusters/:id raw body). */
    document: (id: string) => [...queryKeys.clusters.all, 'document', id] as const,
    bySchool: (schoolId: string) => [...queryKeys.clusters.all, 'bySchool', schoolId] as const,
  },
  /** Pathways (Specializations) — detail cache. */
  pathways: {
    all: ['/pathways'] as const,
    details: () => [...queryKeys.pathways.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.pathways.details(), id] as const,
    /** Full pathway JSON for staff edits (requirements, etc.). */
    document: (id: string) => [...queryKeys.pathways.all, 'document', id] as const,
  },
} as const;

// Type helpers for query key inference
export type QueryKeys = typeof queryKeys;
export type CourseQueryKeys = QueryKeys['courses'];
export type StudentQueryKeys = QueryKeys['students'];
